"use client";

import { ChevronRight, type LucideIcon } from "lucide-react";
import { motion } from "framer-motion";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useSidebar } from "@/components/ui/sidebar";
import * as React from "react";

export function NavMain({
  items,
  title,
}: {
  items: {
    title: string;
    url: string;
    icon?: LucideIcon;
    isActive?: boolean;
    items?: {
      title: string;
      url: string;
    }[];
  }[];
  /** Pass empty string to omit the section label (e.g. top "Overview" block). */
  title: string;
}) {
  const pathname = useLocation().pathname;
  const { isMobile, setOpenMobile } = useSidebar();

  const handleNavigate = React.useCallback(() => {
    if (isMobile) {
      setTimeout(() => setOpenMobile(false), 0);
    }
  }, [isMobile, setOpenMobile]);

  return (
    <SidebarGroup>
      {title ? (
        <SidebarGroupLabel>{title}</SidebarGroupLabel>
      ) : null}
      <SidebarMenu className="gap-0.5">
        {items.map((item) => (
          <React.Fragment key={item.title}>
            {!item.items ? (
              <SidebarMenuItem>
                <TooltipProvider delayDuration={600}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <SidebarMenuButton
                        asChild
                        className={cn(
                          "nav-main-root",
                          pathname === item.url && "nav-main-root--active"
                        )}
                      >
                        <Link
                          to={item.url}
                          className="flex min-w-0 items-center gap-3"
                          onClick={handleNavigate}
                        >
                          {item.icon ? (
                            <item.icon className="size-5 shrink-0 text-[#6B7280] transition-colors" />
                          ) : null}
                          <span className="truncate text-[14px] font-medium opacity-100 group-data-[collapsible=icon]:opacity-0 transition-opacity duration-100">
                            {item.title}
                          </span>
                        </Link>
                      </SidebarMenuButton>
                    </TooltipTrigger>
                    <TooltipContent side="right" sideOffset={8}>
                      {item.title}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </SidebarMenuItem>
            ) : (
              <Collapsible
                asChild
                className="group/collapsible"
                defaultOpen={item.isActive}
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      className={cn(
                        "nav-main-parent",
                        item.isActive && "nav-main-parent--section-active"
                      )}
                    >
                      {item.icon ? (
                        <item.icon className="size-5 shrink-0 text-[#6B7280] transition-colors" />
                      ) : null}
                      <span className="min-w-0 flex-1 truncate text-left text-[14px] font-medium opacity-100 group-data-[collapsible=icon]:opacity-0 transition-opacity duration-100">
                        {item.title}
                      </span>
                      <ChevronRight className="ml-auto size-4 shrink-0 text-[#9CA3AF] transition-transform duration-100 group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {item.items?.map((subItem, index) => (
                        <motion.li
                          key={subItem.title}
                          initial={{ opacity: 0, x: -4 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{
                            duration: 0.12,
                            delay: index * 0.02,
                            ease: "easeOut",
                          }}
                        >
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={pathname === subItem.url}
                              size="sm"
                            >
                              <Link
                                to={subItem.url}
                                title={subItem.title}
                                onClick={handleNavigate}
                              >
                                <span className="truncate pl-0.5 font-medium">
                                  {subItem.title}
                                </span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        </motion.li>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            )}
          </React.Fragment>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}
